import { gql } from "@apollo/client"
import {
    startDraftSyncManager,
    registerDraftRecoveryFunction,
    registerFallbackSaveFunction,
} from "lib/@offline/draft-sync-manager"
import { mutate } from "lib/graphql/mutate"
import { query } from "lib/graphql/query"
import { saveRegimeTailoringPayload } from "routes/schedule/controller/save-regime-tailoring-payload"
import { getGroupPayload } from "routes/schedule/controller/get-group-payload"
import { RootPlugs } from "slot-definitions"
import { BackgroundConflictHandler } from "lib/@offline/background-conflict-handler"
import { isSame } from "library/normalized-stringify/index.mjs"

startDraftSyncManager()

// Mount the conflict resolution dialog at the app root so background sync
// conflicts (when no editor is mounted) still show the user a prompt.
RootPlugs.plug(<BackgroundConflictHandler $lazy />)

// Fallback save for custom schedule drafts that need to sync after reload/navigation
// when no editor component is mounted. Uses saveContext stored in the draft record.
registerFallbackSaveFunction("custom-schedule", async (payload, ctx, options = {}) => {
    const toSave = { ...payload }
    toSave.legislation ??= []
    toSave.legislation = toSave.legislation.unique()

    return mutate(
        gql`
            mutation updateDraft($id: String!, $content: JSONObject!, $expectedVersion: Int, $forceOverwrite: Boolean) {
                writeContent(
                    id: $id
                    content: $content
                    expectedVersion: $expectedVersion
                    forceOverwrite: $forceOverwrite
                )
            }
        `,
        {
            content: toSave,
            id: ctx.code,
            expectedVersion: ctx.expectedVersion,
            forceOverwrite: options.forceOverwrite,
        },
        { returns: "writeContent" }
    )
})

// Fallback save for regime tailoring drafts that need to sync after reload/navigation
// when no editor component is mounted. Uses saveContext stored in the draft record.
registerFallbackSaveFunction("regime-tailoring", async (payload, ctx, options = {}) =>
    saveRegimeTailoringPayload(payload, ctx, options)
)

registerDraftRecoveryFunction("custom-schedule", async ({ draft, result }) => {
    const ctx = draft?.saveContext
    if (!ctx?.code) return null

    const serverDraft = result?.serverData ?? (await getRawDraft(ctx.code))
    if (!serverDraft) return null

    const serverVersion = Number(serverDraft?._draftVersion ?? result?.draftVersion)
    if (sameCustomSchedulePayload(draft.data, serverDraft)) {
        return {
            status: "matched",
            serverVersion: Number.isInteger(serverVersion) ? serverVersion : draft.serverVersion,
        }
    }

    if (Number.isInteger(serverVersion) && serverVersion >= 0 && ctx.expectedVersion !== serverVersion) {
        return {
            status: "retry",
            serverVersion,
            saveContext: {
                ...ctx,
                expectedVersion: serverVersion,
            },
        }
    }

    return result?.conflict ? { status: "conflict" } : null
})

registerDraftRecoveryFunction("regime-tailoring", async ({ draft, result }) => {
    const ctx = draft?.saveContext
    const key = ctx?.globalTailoring ? ctx?.id : ctx?.scheduleId
    if (!ctx?.groupId || !key || !ctx?.treeId) return null

    const serverPayload = await getGroupPayload(ctx.groupId, key, ctx.treeId)
    const serverVersion = Number(result?.treeVersion)
    if (serverPayload && isSame(draft.data, serverPayload)) {
        return {
            status: "matched",
            serverVersion: Number.isInteger(serverVersion) ? serverVersion : draft.serverVersion,
        }
    }

    if (Number.isInteger(serverVersion) && serverVersion >= 0 && ctx.expectedTreeVersion !== serverVersion) {
        return {
            status: "retry",
            serverVersion,
            saveContext: {
                ...ctx,
                expectedTreeVersion: serverVersion,
            },
        }
    }

    return result?.conflict ? { status: "conflict" } : null
})

function getRawDraft(id) {
    return query(
        gql`
            query getRawDraft($id: String!) {
                getRawDraft(id: $id)
            }
        `,
        { id },
        { returns: "getRawDraft", fetchPolicy: "network-only" }
    )
}

function sameCustomSchedulePayload(localPayload, serverPayload) {
    return isSame(localPayload, serverPayload, {
        omit: (key) =>
            [
                "approveAllStatus",
                "draftReason",
                "submissionAuditCreated",
                "rejectionReason",
                "canLock",
                "_draftVersion",
            ].includes(key),
    })
}
